const DATA_URL_PREFIX = "data:";
const BLOB_URL_PREFIX = "blob:";

const isDataOrBlobUrl = (value = "") =>
  value.startsWith(DATA_URL_PREFIX) || value.startsWith(BLOB_URL_PREFIX);

const toAbsoluteUrl = (value = "") => {
  if (!value) return "";

  try {
    return new URL(value, window.location.href).href;
  } catch {
    return value;
  }
};

const parseBackgroundUrls = (backgroundValue = "") => {
  const matches = backgroundValue.match(/url\((['"]?)(.*?)\1\)/g) || [];

  return matches
    .map((match) =>
      match.replace(/^url\((['"]?)/, "").replace(/(['"]?)\)$/, ""),
    )
    .filter(Boolean)
    .map((url) => toAbsoluteUrl(url.trim()));
};

const fetchAsDataUrl = async (url, cache) => {
  if (!url || isDataOrBlobUrl(url)) return url;

  if (cache.has(url)) return cache.get(url);

  const valuePromise = fetch(url, {
    mode: "cors",
    credentials: "omit",
    cache: "force-cache",
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch asset: ${url}`);
      }

      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    })
    .catch(() => url);

  cache.set(url, valuePromise);
  return valuePromise;
};

const inlineImageSources = async (rootElement, cache) => {
  if (!rootElement) return;

  const images = Array.from(rootElement.querySelectorAll("img"));

  await Promise.all(
    images.map(async (image) => {
      const src = toAbsoluteUrl(image.getAttribute("src") || "");
      if (!src || isDataOrBlobUrl(src)) return;

      const inlinedSrc = await fetchAsDataUrl(src, cache);
      if (inlinedSrc) {
        image.setAttribute("src", inlinedSrc);
      }
    }),
  );
};

const inlineBackgroundImages = async (rootElement, cache) => {
  if (!rootElement) return;

  const nodes = [rootElement, ...Array.from(rootElement.querySelectorAll("*"))];

  await Promise.all(
    nodes.map(async (node) => {
      const computedBackground = window.getComputedStyle(node).backgroundImage;
      if (!computedBackground || computedBackground === "none") return;

      const urls = parseBackgroundUrls(computedBackground);
      if (urls.length === 0) return;

      let nextBackground = computedBackground;

      for (const url of urls) {
        const inlinedUrl = await fetchAsDataUrl(url, cache);
        const escapedSource = url.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        nextBackground = nextBackground.replace(
          new RegExp(`url\\((['\"]?)${escapedSource}\\1\\)`, "g"),
          `url("${inlinedUrl}")`,
        );
      }

      node.style.backgroundImage = nextBackground;
    }),
  );
};

export const waitForRender = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

export const waitForFonts = async () => {
  if (!document?.fonts?.ready) return;

  try {
    await document.fonts.ready;
  } catch {
    // Continue export even when font readiness fails.
  }
};

export const waitForImages = async (rootElement) => {
  if (!rootElement) return;

  const images = Array.from(rootElement.querySelectorAll("img"));
  await Promise.all(
    images.map((image) => {
      if (image.complete) return Promise.resolve();

      return new Promise((resolve) => {
        image.onload = resolve;
        image.onerror = resolve;
      });
    }),
  );
};

export const inlineAssetsForCapture = async (rootElement) => {
  if (!rootElement) return;

  const cache = new Map();
  await inlineImageSources(rootElement, cache);
  await inlineBackgroundImages(rootElement, cache);
};